import {useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {PaqueteSelect} from '@/components/bateria/paquete-select'
import {ListaCandidatos} from '@/components/bateria/cola/lista-candidatos'
import {NavegacionTarjeta} from '@/components/bateria/cola/posicion-tarjeta'
import {CropImage} from '@/components/bateria/tira-opciones'
import {IndicadorDeTransito} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {HelpIcon} from '@/components/help-icon'
import {displayFilename} from '@/lib/bateria'
import {cn} from '@/lib/utils'
import type {CandidatoIdentidad, PaqueteDestino, TarjetaCola} from '@/lib/bateria-cola'
/** `ref` dice de qué hoja salió el destino: quien lo recibe rechaza el de otra hoja. */
export type DestinoHoja = {ref: string; paqueteId: string; persona: string; numero: number}

/** v2 §1.4. El botón nombra a la persona y la página, nunca «Asignar» a secas: una hoja mal asignada es peor que una en cuarentena. */
export function TarjetaUbicar({
  tarjeta,
  paquetes,
  posicion,
  seleccion,
  candidatos,
  buscandoCandidatos,
  onElegirCandidato,
  onAsignar,
  onDescartar,
  descarteArmado,
  onDespues,
  onAtras,
  onAdelante,
  buscando,
  onBuscar,
  pendiente,
  puedeDecidir,
}: {
  tarjeta: TarjetaCola
  paquetes: PaqueteDestino[]
  posicion: ReactNode
  seleccion: DestinoHoja | null
  puedeDecidir: boolean
  candidatos: CandidatoIdentidad[]
  buscandoCandidatos: boolean
  onElegirCandidato: (indice: number) => void
  onAsignar: (destino: DestinoHoja) => void
  onDescartar: () => void
  descarteArmado: boolean
  onDespues: () => void
  onAtras: () => void
  onAdelante: () => void
  buscando: boolean
  onBuscar: (abierto: boolean) => void
  pendiente: boolean
}) {
  const {t} = useTranslation()
  const [numeroManual, setNumeroManual] = useState('')
  const [numeroAfirmado, setNumeroAfirmado] = useState('')
  const [paqueteManual, setPaqueteManual] = useState<PaqueteDestino | null>(null)
  // Subida directa al paquete: la identidad ya está dicha y el id viaja con la tarjeta, sin buscar por nombre.
  const afirmado =
    tarjeta.paquete_afirmado_id && tarjeta.persona_corta
      ? {paqueteId: tarjeta.paquete_afirmado_id, persona: tarjeta.persona_corta}
      : null
  const numeroDeLaAfirmada = Number(numeroAfirmado)
  const numeroAfirmadoValido = Number.isInteger(numeroDeLaAfirmada) && numeroDeLaAfirmada >= 1
  // Un destino elegido sobre otra hoja no se nombra ni se habilita.
  const destino = seleccion && seleccion.ref === tarjeta.ref ? seleccion : null

  function confirmarManual() {
    const numero = Number(numeroManual)
    if (!paqueteManual || !Number.isInteger(numero) || numero < 1) return
    onAsignar({ref: tarjeta.ref, paqueteId: paqueteManual.id, persona: paqueteManual.employee_name, numero})
  }

  return (
    <section className="surface" data-testid="cola-tarjeta-ubicar">
      <header className="surface-divider flex flex-wrap items-center gap-2 border-b px-5 py-3.5">
        <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
          {t('admin.bateria.cola.seccion.ubicar')}
        </span>
        {tarjeta.motivo_cuarentena && (
          <Tag tone="amber" data-testid="cola-ubicar-motivo">
            {t(`admin.bateria.cuarentena.motivo.${tarjeta.motivo_cuarentena}`, {
              defaultValue: tarjeta.motivo_cuarentena,
            })}
          </Tag>
        )}
        {tarjeta.etapa_cuarentena && (
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="cola-ubicar-etapa">
            {t(`admin.bateria.cola.etapa.${tarjeta.etapa_cuarentena}`, {defaultValue: tarjeta.etapa_cuarentena})}
          </span>
        )}
        {posicion}
      </header>

      <div className="space-y-4 px-5 py-4">
        {tarjeta.crop_download_url ? (
          <a
            href={tarjeta.pagina_pdf_url ?? tarjeta.crop_download_url}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block cursor-zoom-in"
            data-testid="cola-ubicar-lupa">
            <IndicadorDeTransito />
            <CropImage
              url={tarjeta.crop_download_url}
              alt={t('admin.bateria.cuarentena.miniaturaAlt')}
              fallback={t('admin.bateria.cuarentena.abrirPagina')}
              testId="cola-ubicar-miniatura"
              marco
              pie={t('admin.bateria.cola.ubicar.hojaEscaneada')}
            />
          </a>
        ) : (
          <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            {t('admin.bateria.cuarentena.sinPreview')}
          </p>
        )}

        {afirmado && (
          <div
            className="space-y-2 rounded-lg border border-dashed border-border p-3"
            data-testid="cola-ubicar-afirmada">
            <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              {t('admin.bateria.cola.ubicar.afirmadaPara', {persona: afirmado.persona})}
              <HelpIcon
                label={t('common.help.iconLabelFor', {
                  campo: t('admin.bateria.cola.ubicar.afirmadaPara', {persona: afirmado.persona}),
                })}
                testId="cola-ubicar-afirmada-tip">
                {t('admin.bateria.cola.ubicar.afirmadaTip')}
              </HelpIcon>
            </p>
            <Input
              data-testid="cola-ubicar-numero-afirmado"
              type="number"
              min={1}
              value={numeroAfirmado}
              onChange={(e) => setNumeroAfirmado(e.target.value)}
              placeholder={t('admin.bateria.cuarentena.numeroPlaceholder')}
              aria-label={t('admin.bateria.cola.ubicar.numeroLabel')}
              className="font-mono"
            />
            <Button
              size="sm"
              data-testid="cola-ubicar-asignar-afirmada"
              disabled={!numeroAfirmadoValido || pendiente || !puedeDecidir}
              onClick={() =>
                onAsignar({
                  ref: tarjeta.ref,
                  paqueteId: afirmado.paqueteId,
                  persona: afirmado.persona,
                  numero: numeroDeLaAfirmada,
                })
              }>
              {t('admin.bateria.cola.ubicar.asignarAfirmada', {numero: numeroAfirmado || '—'})}
            </Button>
          </div>
        )}

        {buscandoCandidatos ? (
          <p className="font-mono text-[11px] text-muted-foreground" data-testid="cola-ubicar-buscando-candidatos">
            {t('admin.bateria.cola.ubicar.buscandoCandidatos')}
          </p>
        ) : (
          <ListaCandidatos
            candidatos={candidatos}
            paqueteElegido={destino?.paqueteId ?? null}
            onElegir={onElegirCandidato}
            testId="cola-ubicar"
            deshabilitada={!puedeDecidir}
          />
        )}

        {buscando && (
          <div className="space-y-3 rounded-lg border border-dashed border-border p-3" data-testid="cola-ubicar-buscar">
            <PaqueteSelect
              paquetes={paquetes}
              value={paqueteManual}
              testId="cola-ubicar-roster"
              onPick={(p) => setPaqueteManual(p)}
            />
            <Input
              data-testid="cola-ubicar-numero"
              type="number"
              min={1}
              value={numeroManual}
              onChange={(e) => setNumeroManual(e.target.value)}
              placeholder={t('admin.bateria.cuarentena.numeroPlaceholder')}
              className="font-mono"
            />
            <Button
              size="sm"
              data-testid="cola-ubicar-confirmar-manual"
              onClick={confirmarManual}
              disabled={pendiente || !puedeDecidir}>
              {t('admin.bateria.cola.ubicar.confirmarManual')}
            </Button>
          </div>
        )}

        {/* Descartar no tiene vuelta atrás: repetir el gesto lo confirma, sin modal que rompa el ritmo. */}
        {descarteArmado && (
          <p className="notice-amber rounded-md px-3 py-2 text-xs" data-testid="cola-ubicar-descarte-armado">
            {t('admin.bateria.cola.ubicar.descartarAviso')}
          </p>
        )}

        {/* Una copia idéntica esperando detrás haría parecer que el descarte falló: se dice. */}
        {tarjeta.archivo_origen && (
          <p className="font-mono text-[11px] text-muted-foreground" data-testid="cola-ubicar-archivo">
            {t('admin.bateria.cola.ubicar.archivo', {archivo: displayFilename(tarjeta.archivo_origen)})}
          </p>
        )}
        {(tarjeta.gemelas ?? 1) > 1 && (
          <p className="notice-amber rounded-md px-3 py-2 text-xs" data-testid="cola-ubicar-gemelas">
            {t('admin.bateria.cola.ubicar.gemelas', {count: (tarjeta.gemelas ?? 1) - 1})}
          </p>
        )}
      </div>

      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <NavegacionTarjeta onAtras={onAtras} onAdelante={onAdelante} />
          <Button
            size="sm"
            variant="outline"
            className="min-h-11 md:min-h-0"
            data-testid="cola-ubicar-buscar-abrir"
            onClick={() => onBuscar(!buscando)}>
            {t('admin.bateria.cola.ubicar.buscar')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className={cn('min-h-11 md:min-h-0', descarteArmado && 'border border-dashed border-tone-amber-fg')}
            data-testid="cola-ubicar-descartar"
            disabled={!puedeDecidir}
            onClick={onDescartar}>
            {descarteArmado
              ? t('admin.bateria.cola.ubicar.descartarConfirmar')
              : t('admin.bateria.cola.ubicar.descartar')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="min-h-11 md:min-h-0"
            data-testid="cola-despues"
            onClick={onDespues}>
            {t('admin.bateria.cola.despues')}
          </Button>
        </div>
        <Button
          className="min-h-11 md:min-h-0"
          data-testid="cola-ubicar-asignar"
          disabled={!destino || pendiente || !puedeDecidir}
          onClick={() => destino && onAsignar(destino)}>
          {destino
            ? t('admin.bateria.cola.ubicar.asignarA', {persona: destino.persona, numero: destino.numero})
            : t('admin.bateria.cola.ubicar.elijaDestino')}
        </Button>
      </div>
    </section>
  )
}
